import type { Room } from "livekit-client";
import { RoomEvent } from "livekit-client";
import { useCallback } from "react";

type SpatiusEventsLogger = {
  debug: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
};

type MutableRef<T> = {
  current: T;
};

type SpatiusPrimarySessionSetter<TSession> = (session: TSession | null) => void;

type UseLiveKitSpatiusPrimaryEventsInput<TSession> = {
  currentRoomRef: MutableRef<Room>;
  spatiusPrimaryViewerRoomRef: MutableRef<Room | null>;
  spatiusPrimaryParticipantConnectedHandlerRef: MutableRef<(() => void) | null>;
  spatiusPrimaryTrackPublishedHandlerRef: MutableRef<(() => void) | null>;
  spatiusPrimaryTrackSubscribedHandlerRef: MutableRef<(() => void) | null>;
  spatiusPrimaryConnectedRef: MutableRef<boolean>;
  connectionFailurePhaseRef: MutableRef<string>;
  setExternalRoom: (room: Room | null) => void;
  setSpatiusPrimarySession: SpatiusPrimarySessionSetter<TSession>;
  clearSpatiusPrimaryFallbackTimer: () => void;
  clearSpatiusPrimaryBaseAudioWatchdogTimer: () => void;
  syncSpatiusPrimaryViewerSubscriptions: (viewerRoom: Room, reason?: string) => void;
  scheduleSpatiusPrimaryBaseAudioWatchdog: (reason: string) => void;
  markLatencyPhase: (phase: string, data?: Record<string, unknown>) => void;
  captureLiveKitConnectionEvent: (
    eventName: string,
    error: unknown,
    extra?: Record<string, unknown>
  ) => void;
  fallbackToAudioOnly: (reason: "error" | "timeout") => Promise<void>;
  logger: SpatiusEventsLogger;
};

export function useLiveKitSpatiusPrimaryEvents<TSession>({
  currentRoomRef,
  spatiusPrimaryViewerRoomRef,
  spatiusPrimaryParticipantConnectedHandlerRef,
  spatiusPrimaryTrackPublishedHandlerRef,
  spatiusPrimaryTrackSubscribedHandlerRef,
  spatiusPrimaryConnectedRef,
  connectionFailurePhaseRef,
  setExternalRoom,
  setSpatiusPrimarySession,
  clearSpatiusPrimaryFallbackTimer,
  clearSpatiusPrimaryBaseAudioWatchdogTimer,
  syncSpatiusPrimaryViewerSubscriptions,
  scheduleSpatiusPrimaryBaseAudioWatchdog,
  markLatencyPhase,
  captureLiveKitConnectionEvent,
  fallbackToAudioOnly,
  logger,
}: UseLiveKitSpatiusPrimaryEventsInput<TSession>) {
  const handleSpatiusPrimaryConnected = useCallback(
    (connectedRoom: Room | null) => {
      if (!connectedRoom) {
        return;
      }

      const previousViewerRoom = spatiusPrimaryViewerRoomRef.current;
      const previousParticipantConnectedHandler =
        spatiusPrimaryParticipantConnectedHandlerRef.current;
      const previousTrackPublishedHandler = spatiusPrimaryTrackPublishedHandlerRef.current;
      const previousTrackSubscribedHandler = spatiusPrimaryTrackSubscribedHandlerRef.current;

      if (previousViewerRoom && previousParticipantConnectedHandler) {
        previousViewerRoom.off(RoomEvent.ParticipantConnected, previousParticipantConnectedHandler);
      }

      if (previousViewerRoom && previousTrackPublishedHandler) {
        previousViewerRoom.off(RoomEvent.TrackPublished, previousTrackPublishedHandler);
      }

      if (previousViewerRoom && previousTrackSubscribedHandler) {
        previousViewerRoom.off(RoomEvent.TrackSubscribed, previousTrackSubscribedHandler);
      }

      markLatencyPhase("spatius_primary_connected", {
        roomName: connectedRoom.name,
      });
      clearSpatiusPrimaryFallbackTimer();
      spatiusPrimaryConnectedRef.current = true;
      syncSpatiusPrimaryViewerSubscriptions(connectedRoom, "spatius_primary_connected");
      scheduleSpatiusPrimaryBaseAudioWatchdog("spatius_primary_connected");

      try {
        currentRoomRef.current.localParticipant.setAttributes({ avatar_ready: "true" });
      } catch (attrError) {
        logger.warn("Failed to set avatar_ready=true attribute", {
          error: attrError instanceof Error ? attrError.message : String(attrError),
        });
      }

      const handleParticipantConnected = () => {
        syncSpatiusPrimaryViewerSubscriptions(
          connectedRoom,
          "spatius_viewer_participant_connected"
        );
      };
      const handleTrackPublished = () => {
        syncSpatiusPrimaryViewerSubscriptions(connectedRoom, "spatius_viewer_track_published");
      };
      const handleTrackSubscribed = () => {
        syncSpatiusPrimaryViewerSubscriptions(connectedRoom, "spatius_viewer_track_subscribed");
      };

      connectedRoom.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
      connectedRoom.on(RoomEvent.TrackPublished, handleTrackPublished);
      connectedRoom.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);

      spatiusPrimaryViewerRoomRef.current = connectedRoom;
      spatiusPrimaryParticipantConnectedHandlerRef.current = handleParticipantConnected;
      spatiusPrimaryTrackPublishedHandlerRef.current = handleTrackPublished;
      spatiusPrimaryTrackSubscribedHandlerRef.current = handleTrackSubscribed;
      setExternalRoom(null);
    },
    [
      clearSpatiusPrimaryFallbackTimer,
      currentRoomRef,
      logger,
      markLatencyPhase,
      scheduleSpatiusPrimaryBaseAudioWatchdog,
      setExternalRoom,
      spatiusPrimaryConnectedRef,
      spatiusPrimaryParticipantConnectedHandlerRef,
      spatiusPrimaryTrackPublishedHandlerRef,
      spatiusPrimaryTrackSubscribedHandlerRef,
      spatiusPrimaryViewerRoomRef,
      syncSpatiusPrimaryViewerSubscriptions,
    ]
  );

  const handleSpatiusPrimaryStateChange = useCallback(
    (status: "idle" | "initializing" | "connecting" | "connected" | "disconnecting" | "error") => {
      logger.debug("Spatius primary state changed", {
        status,
      });
    },
    [logger]
  );

  const handleSpatiusPrimaryDisconnected = useCallback(() => {
    markLatencyPhase("spatius_primary_disconnected");
    clearSpatiusPrimaryFallbackTimer();
    clearSpatiusPrimaryBaseAudioWatchdogTimer();
    spatiusPrimaryConnectedRef.current = false;
    setExternalRoom(null);
    setSpatiusPrimarySession(null);
  }, [
    clearSpatiusPrimaryBaseAudioWatchdogTimer,
    clearSpatiusPrimaryFallbackTimer,
    markLatencyPhase,
    setExternalRoom,
    setSpatiusPrimarySession,
    spatiusPrimaryConnectedRef,
  ]);

  const handleSpatiusPrimaryError = useCallback(
    (error: Error) => {
      connectionFailurePhaseRef.current = "spatius_primary_connect";
      markLatencyPhase("spatius_primary_error", {
        error: error.message,
      });
      captureLiveKitConnectionEvent("spatius_viewer_connect_failed", error, {
        spatius_primary_connected: spatiusPrimaryConnectedRef.current,
      });
      logger.error("Spatius primary session failed", {
        error: error.message,
      });

      if (!spatiusPrimaryConnectedRef.current) {
        void fallbackToAudioOnly("error");
        return;
      }
      logger.warn("Spatius viewer errored after connecting; keeping audio session active", {
        error: error.message,
      });
    },
    [
      captureLiveKitConnectionEvent,
      connectionFailurePhaseRef,
      fallbackToAudioOnly,
      logger,
      markLatencyPhase,
      spatiusPrimaryConnectedRef,
    ]
  );

  return {
    handleSpatiusPrimaryConnected,
    handleSpatiusPrimaryDisconnected,
    handleSpatiusPrimaryError,
    handleSpatiusPrimaryStateChange,
  };
}
